import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  merge,
  startWith,
  switchMap,
  catchError,
  of as observableOf,
  Subject,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { ProjectService } from 'src/app/private/services/project.service';
import { UsersService } from 'src/app/private/services/users.service';
import { BugService } from 'src/app/private/services/bug.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { EpicService } from 'src/app/private/services/epic.service';
import { ProjectFeatureService } from 'src/app/private/services/project-feature.service';
import { UserStoryService } from 'src/app/private/services/user-story.service';

import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { appendCenterScopeToPayload } from 'src/app/utils/tenant-scope.util';

@Component({
  selector: 'app-project-tasks',
  templateUrl: './project-tasks.component.html',
  styleUrls: ['./project-tasks.component.scss'],
})
export class ProjectTasksComponent implements OnInit, AfterViewInit, OnDestroy {
  projectId: any;
  projectData: any;

  private destroy$ = new Subject<void>();
  private trigger$ = new Subject<void>();

  // ── Table ──────────────────────────────────────────────────────────────────
  selectedTab = 0;

  displayedColumns = [
    'task_key',
    'title',
    'user_story',
    'type',
    'priority',
    'status',
    'assignee',
    'progress',
    'due_date',
    'created_at',
    'action',
  ];

  dataSource = new MatTableDataSource<any>([]);

  totalRows = 0;
  pageSize = 25;
  pageSizeOptions = [10, 25, 100];
  currentPage = 0;

  searchText: any = '';

  reloading = false;
  initialLoad = true;
  isAdmin = false;

  // ── Editor ─────────────────────────────────────────────────────────────────
  public Editor = ClassicEditor as unknown as any;

  editorConfig: any = {
    toolbar: {
      items: [
        'heading',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'alignment',
        '|',
        'numberedList',
        'bulletedList',
        '|',
        'outdent',
        'indent',
        '|',
        'link',
        'blockQuote',
        'insertTable',
        '|',
        'undo',
        'redo',
      ],
      shouldNotGroupWhenFull: true,
    },

    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Heading 4',
          class: 'ck-heading_heading4',
        },
      ],
    },

    fontSize: {
      options: ['tiny', 'small', 'default', 'big', 'huge'],
    },

    fontColor: {
      colors: [
        { color: '#000000', label: 'Black' },
        { color: '#4D4D4D', label: 'Dark Grey' },
        { color: '#999999', label: 'Grey' },
        { color: '#FFFFFF', label: 'White', hasBorder: true },
        { color: '#E74C3C', label: 'Red' },
        { color: '#E67E22', label: 'Orange' },
        { color: '#F39C12', label: 'Yellow' },
        { color: '#27AE60', label: 'Green' },
        { color: '#3498DB', label: 'Blue' },
        { color: '#9B59B6', label: 'Purple' },
      ],
    },

    fontBackgroundColor: {
      colors: [
        { color: '#FFFFFF', label: 'White', hasBorder: true },
        { color: '#FFEBEE', label: 'Light Red' },
        { color: '#FFF3E0', label: 'Light Orange' },
        { color: '#FFF9C4', label: 'Light Yellow' },
        { color: '#E8F5E9', label: 'Light Green' },
        { color: '#E3F2FD', label: 'Light Blue' },
        { color: '#F3E5F5', label: 'Light Purple' },
      ],
    },

    alignment: {
      options: ['left', 'center', 'right', 'justify'],
    },

    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
    },

    placeholder: 'Enter description here...',
  };

  // ── Filter Data ────────────────────────────────────────────────────────────
  /** Raw project member users for assignee/reporter dropdowns */
  projectMembers: any[] = [];
  /** key/value options for table filters */
  users: any[] = [];
  modules: any[] = [];
  departments: any[] = [];
  projectBugs: any[] = [];

  breadcrumbArray: any[] = [];
  filterArray: any[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  taskTabs = [
    { label: 'All Tasks', value: 'all' },
    { label: 'To Do', value: 'todo' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'In Review', value: 'in_review' },
    { label: 'Completed', value: 'completed' },
    { label: 'Blocked', value: 'blocked' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  // ── Modal / Form State ────────────────────────────────────────────────────
  formAction = 'Add';

  editData: any = {};

  currentTaskId: any;

  btnLoading = false;
  detailsLoading = false;

  activeTaskTab = 1;

  // ── Attachments ────────────────────────────────────────────────────────────
  showPreview = false;
  selectedIndex = 0;

  // ── Project Hierarchy State ────────────────────────────────────────────────
  epics: any[] = [];
  formFeatures: any[] = [];
  formUserStories: any[] = [];

  selectedFormEpicId: any = null;
  selectedFormFeatureId: any = null;

  // For List Filters
  listEpics: any[] = [];
  listFeatures: any[] = [];
  listUserStories: any[] = [];

  selectedListEpicId: any = null;
  selectedListFeatureId: any = null;
  selectedListUserStoryId: any = null;

  constructor(
    private route: ActivatedRoute,
    private api: ProjectTaskService,
    private projectService: ProjectService,
    private userService: UsersService,
    private bugService: BugService,
    private localStorage: LocalStorageService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private epicService: EpicService,
    private featureService: ProjectFeatureService,
    private userStoryService: UserStoryService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('project_id');
    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole(),
    );

    this.loadProject();
    this.loadFilterData();
    this.loadEpics();
  }

  ngAfterViewInit() {
    this.loadTaskList();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  prettyAction(type: string) {
    if (!type) return '';

    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  changeTab(index: number) {
    this.selectedTab = index;

    this.paginator.pageIndex = 0;

    this.trigger$.next();
  }

  onStatusChange(status: string) {
    if (status === 'todo') {
      this.editData.progress_percentage = 0;
    } else if (status === 'in_progress') {
      this.editData.progress_percentage = 50;
    } else if (status === 'in_review') {
      this.editData.progress_percentage = 90;
    } else if (status === 'completed') {
      this.editData.progress_percentage = 100;
    }
  }

  searchFilter(value: any) {
    this.searchText = value;

    this.paginator.pageIndex = 0;

    this.trigger$.next();
  }

  // ── Load Project ───────────────────────────────────────────────────────────

  loadProject() {
    this.projectService
      .getProjectById({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        user_id: this.localStorage.getUserId(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.projectData = data;

          this.breadcrumbArray = [
            { label: 'Projects', link: '/main/manage-projects' },
            {
              label: this.projectData?.name || 'Project Tasks',
              link: '/main/manage-project-tasks/' + this.projectId,
            },
          ];
        },

        error: () => {
          this.toastr.error('Failed to load project');
        },
      });
  }

  // ── Load Modules ───────────────────────────────────────────────────────────

  loadModules() {
    this.projectService
      .getProjectModuleList({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const list = res?.data
            ? res.data
            : Array.isArray(res)
              ? res
              : res?.rows
                ? res.rows
                : [];
          this.modules = list.map((el: any) => ({
            key: el.module_id || el.id,
            value: el.module_name || el.name || el.title,
          }));

          this.syncFilterOptions();
        },

        error: () => {
          this.toastr.error('Failed to load modules');
        },
      });
  }

  // ── Load Users ─────────────────────────────────────────────────────────────

  loadUsers() {
    if (!this.projectId) return;

    this.projectService
      .getProjectMembers({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        user_id: this.localStorage.getUserId(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.projectMembers =
            this.projectService.parseProjectMembersResponse(data);
          this.users = this.projectMembers.map((user: any) => ({
            key: user.user_id,
            value: `${user.fname || ''} ${user.lname || ''}`.trim(),
          }));

          this.syncFilterOptions();
          this.cdr.detectChanges();
        },

        error: () => {
          this.toastr.error('Failed to load project members');
        },
      });
  }

  // ── Load Departments ───────────────────────────────────────────────────────

  loadDepartments() {
    this.userService
      .getAllDepartment({
        client_id: this.localStorage.getClientId(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (raw: any) => {
          const list = raw.data ? raw.data : Array.isArray(raw) ? raw : [];

          this.departments = list.map((el: any) => ({
            key: el.department_id || el.dept_id,
            value: el.dept_name || el.name,
          }));
        },

        error: () => {
          this.toastr.error('Failed to load departments');
        },
      });
  }

  // ── Load Bugs ──────────────────────────────────────────────────────────────

  loadBugs() {
    this.bugService
      .getAllBugs({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        limit: 1000,
        offset: 0,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          const rows = data?.rows ?? [];

          this.projectBugs = rows.map((el: any) => ({
            key: el.bug_id,
            value: el.bug_code + ' - ' + el.title,
          }));
        },

        error: () => {
          this.toastr.error('Failed to load bugs');
        },
      });
  }

  // ── Load Filter Data ───────────────────────────────────────────────────────

  loadFilterData() {
    this.loadModules();
    this.loadUsers();
    this.loadDepartments();
    this.loadBugs();

    this.buildFilterArray();
  }

  private buildFilterArray() {
    const filters: any[] = [
      {
        key: 'task_key',
        value: 'Task Code',
        type: 'text',
      },
    ];

    if (this.isAdmin) {
      filters.push({
        key: 'assignee_id',
        value: 'Select Assignee',
        type: 'select',
        options: this.users,
      });
    }

    filters.push(
      {
        key: 'status',
        value: 'Select Status',
        type: 'select',
        options: [
          { value: 'To Do', key: 'todo' },
          { value: 'In Progress', key: 'in_progress' },
          { value: 'In Review', key: 'in_review' },
          { value: 'Completed', key: 'completed' },
          { value: 'Blocked', key: 'blocked' },
          { value: 'Cancelled', key: 'cancelled' },
        ],
      },
      {
        key: 'priority',
        value: 'Select Priority',
        type: 'select',
        options: [
          { value: 'Low', key: 'low' },
          { value: 'Medium', key: 'medium' },
          { value: 'High', key: 'high' },
          { value: 'Urgent', key: 'urgent' },
        ],
      },
    );

    this.filterArray = filters;
  }

  private syncFilterOptions() {
    const assigneeFilter = this.filterArray.find(
      (f) => f.key === 'assignee_id',
    );

    if (assigneeFilter) {
      assigneeFilter.options = this.users;
    }
  }

  // ── Load Task List ─────────────────────────────────────────────────────────

  loadTaskList() {
    this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.paginator.pageIndex = 0;
    });

    merge(this.sort.sortChange, this.paginator.page, this.trigger$)
      .pipe(
        startWith({}),

        switchMap(() => {
          this.reloading = true;

          return this.api.getAllProjectTasks(this.buildTaskParams()).pipe(
            catchError(() => {
              this.toastr.error('Failed to load tasks');

              return observableOf({
                rows: [],
                total: 0,
              });
            }),
          );
        }),

        takeUntil(this.destroy$),
      )
      .subscribe((res: any) => {
        this.reloading = false;
        this.initialLoad = false;

        const rows = res?.rows ?? res?.data?.rows ?? [];

        this.totalRows = res?.total ?? res?.data?.total ?? 0;

        this.dataSource = new MatTableDataSource(rows);

        this.cdr.detectChanges();
      });
  }

  private buildTaskParams(): any {
    const whereClause: any = {
      client_id: this.localStorage.getClientId(),
      project_id: this.projectId,
    };

    if (this.selectedListEpicId) {
      whereClause.epic_id = this.selectedListEpicId;
    }
    if (this.selectedListFeatureId) {
      whereClause.feature_id = this.selectedListFeatureId;
    }
    if (this.selectedListUserStoryId) {
      whereClause.user_story_id = this.selectedListUserStoryId;
    }

    if (this.selectedTab !== 0) {
      whereClause.status = this.taskTabs[this.selectedTab].value;
    }

    const param: any = {
      page: this.paginator.pageIndex + 1,

      limit: this.paginator.pageSize,

      user_id: this.localStorage.getUserId(),

      where: whereClause,

      search: {
        task_key: this.searchText?.['task_key'] || '',
      },

      order: {
        col: 'updated_at',
        dir: 'DESC',
      },
    };

    if (this.searchText && typeof this.searchText === 'object') {
      for (const key in this.searchText) {
        if (key !== 'task_key' && this.searchText[key]) {
          param.where[key] = this.searchText[key];
        }
      }
    }

    return param;
  }

  // ── Modal Helpers ──────────────────────────────────────────────────────────

  open(content: any) {
    this.formAction = 'Add';

    this.currentTaskId = null;

    this.activeTaskTab = 1;

    this.loadUsers();

    this.selectedFormEpicId = null;
    this.selectedFormFeatureId = null;
    this.formFeatures = [];
    this.formUserStories = [];

    this.editData = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId(),
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      task_type: 'task',
      progress_percentage: 0,
      department_id: null,
      module_id: null,
      assignee_id: null,
      bug_id: null,
      user_story_id: null,
      attachments: [],
    };

    this.modalService.open(content, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });
  }

  openEdit(content: any, data: any) {
    this.formAction = 'Update';
    this.currentTaskId = data.task_id;
    this.activeTaskTab = 1;

    this.loadUsers();

    this.editData = { ...data };
    this.editData.attachments = this.editData.attachments || [];

    if (this.editData.due_date) {
      this.editData.due_date = new Date(this.editData.due_date)
        .toISOString()
        .substring(0, 10);
    }

    if (this.editData.start_date) {
      this.editData.start_date = new Date(this.editData.start_date)
        .toISOString()
        .substring(0, 10);
    }

    this.modalService.open(content, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      scrollable: true,
    });

    this.detailsLoading = true;

    this.api
      .getProjectTaskById({
        where: {
          client_id: this.localStorage.getClientId(),
          project_id: this.projectId,
          task_id: this.currentTaskId,
        },
      })
      .subscribe({
        next: (res: any) => {
          this.detailsLoading = false;

          if (!res) {
            return;
          }

          this.editData = { ...this.editData, ...res };
          this.editData.attachments = this.editData.attachments || [];

          if (res?.user_story) {
            this.selectedFormEpicId =
              res.user_story.feature?.epic?.epic_id || null;
            this.selectedFormFeatureId =
              res.user_story.feature?.feature_id || null;
            this.editData.user_story_id = res.user_story.user_story_id || null;

            if (this.selectedFormEpicId) {
              this.loadFormFeatures(this.selectedFormEpicId, () => {
                if (this.selectedFormFeatureId) {
                  this.loadFormUserStories(this.selectedFormFeatureId);
                }
              });
            }
          } else {
            this.selectedFormEpicId = null;
            this.selectedFormFeatureId = null;
            this.editData.user_story_id = null;
            this.formFeatures = [];
            this.formUserStories = [];
          }

          if (this.editData.due_date) {
            this.editData.due_date = new Date(this.editData.due_date)
              .toISOString()
              .substring(0, 10);
          }

          if (this.editData.start_date) {
            this.editData.start_date = new Date(this.editData.start_date)
              .toISOString()
              .substring(0, 10);
          }
        },

        error: () => {
          this.detailsLoading = false;
          this.toastr.error('Failed to load task details');
        },
      });
  }

  switchTaskTab(tab: number) {
    this.activeTaskTab = tab;
  }

  handleClosDialog() {
    this.modalService.dismissAll();
  }

  openDelete(modal: any, data: any) {
    this.editData = data;

    this.modalService.open(modal, {
      size: 'md',
      centered: true,
      backdrop: 'static',
    });
  }

  // ── Task CRUD ──────────────────────────────────────────────────────────────

  submitTaskForm() {
    this.btnLoading = true;

    if (
      this.editData.start_date &&
      this.editData.due_date &&
      new Date(this.editData.start_date) > new Date(this.editData.due_date)
    ) {
      this.toastr.error('Start date cannot be greater than Due date');
      this.btnLoading = false;
      return;
    }

    const isUpdate = this.formAction === 'Update';

    let payload: any = appendCenterScopeToPayload(
      {
        ...this.editData,
        updated_by: this.localStorage.getUserId(),
        created_by: this.localStorage.getUserId(),
        reporter_id: this.localStorage.getUserId(),
      },
      this.localStorage,
    );

    if (!payload.bug_id) delete payload.bug_id;

    if (!payload.module_id) delete payload.module_id;

    if (!payload.department_id) delete payload.department_id;

    if (!payload.assignee_id) delete payload.assignee_id;

    if (isUpdate) {
      payload.where = {
        task_id: this.currentTaskId,
      };

      this.api.updateProjectTask(payload).subscribe({
        next: () => {
          this.btnLoading = false;

          this.toastr.success('Task updated successfully');

          this.trigger$.next();
        },

        error: (e: any) => {
          this.btnLoading = false;

          this.toastr.error(e?.error?.message || 'Failed to update task');
        },
      });
    } else {
      this.api.createProjectTask(payload).subscribe({
        next: (data: any) => {
          this.btnLoading = false;

          this.toastr.success('Task created successfully');

          if (data?.data?.task_id) {
            this.formAction = 'Update';

            this.currentTaskId = data.data.task_id;
          }

          this.trigger$.next();
        },

        error: (e: any) => {
          this.btnLoading = false;

          this.toastr.error(e?.error?.message || 'Failed to create task');
        },
      });
    }
  }

  confirmDelete() {
    if (!this.editData?.task_id) return;

    this.btnLoading = true;

    this.api
      .deleteProjectTask({
        where: {
          task_id: this.editData.task_id,
        },

        deleted_by: this.localStorage.getUserId(),
      })
      .subscribe({
        next: () => {
          this.btnLoading = false;

          this.toastr.success('Task deleted');

          this.modalService.dismissAll();

          this.trigger$.next();
        },

        error: () => {
          this.btnLoading = false;

          this.toastr.error('Delete failed');
        },
      });
  }

  // ── Attachments ────────────────────────────────────────────────────────────

  onUploadComplete(uploadedFiles: any[]) {
    this.editData.attachments = this.editData.attachments || [];

    this.editData.attachments = [
      ...this.editData.attachments,
      ...uploadedFiles,
    ];
  }

  removeAttachment(index: number) {
    if (this.editData.attachments && this.editData.attachments.length > index) {
      this.editData.attachments.splice(index, 1);
    }
  }

  isImage(item: any): boolean {
    if (!item) return false;

    if (item.type && item.type.startsWith('image/')) {
      return true;
    }

    if (item.url) {
      const ext = item.url.split('.').pop()?.toLowerCase();

      return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext || '');
    }

    return false;
  }

  openPreview(index: number) {
    this.selectedIndex = index;

    this.showPreview = true;
  }

  loadEpics() {
    this.epicService
      .getAllEpics({
        client_id: this.localStorage.getClientId(),
        project_id: this.projectId,
        limit: 1000,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const rows = res?.rows || res?.data?.rows || res?.data || [];
          this.epics = rows;
          this.listEpics = rows;
        },
        error: () => {
          this.toastr.error('Failed to load Epics');
        },
      });
  }

  onListEpicChange(epicId: any) {
    this.selectedListEpicId = epicId ? Number(epicId) : null;
    this.selectedListFeatureId = null;
    this.selectedListUserStoryId = null;
    this.listFeatures = [];
    this.listUserStories = [];

    if (this.selectedListEpicId) {
      this.featureService
        .getAllFeatures({
          client_id: this.localStorage.getClientId(),
          epic_id: this.selectedListEpicId,
          limit: 1000,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.listFeatures = res?.rows || res?.data?.rows || res?.data || [];
          },
        });
    }

    this.paginator.pageIndex = 0;
    this.trigger$.next();
  }

  onListFeatureChange(featureId: any) {
    this.selectedListFeatureId = featureId ? Number(featureId) : null;
    this.selectedListUserStoryId = null;
    this.listUserStories = [];

    if (this.selectedListFeatureId) {
      this.userStoryService
        .getAllUserStories({
          client_id: this.localStorage.getClientId(),
          feature_id: this.selectedListFeatureId,
          limit: 1000,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.listUserStories =
              res?.rows || res?.data?.rows || res?.data || [];
          },
        });
    }

    this.paginator.pageIndex = 0;
    this.trigger$.next();
  }

  onListUserStoryChange(storyId: any) {
    this.selectedListUserStoryId = storyId ? Number(storyId) : null;
    this.paginator.pageIndex = 0;
    this.trigger$.next();
  }

  // ── Form Hierarchy Cascading Helpers ───────────────────────────────────────
  onFormEpicChange(epicId: any) {
    this.selectedFormEpicId = epicId ? Number(epicId) : null;
    this.selectedFormFeatureId = null;
    this.editData.user_story_id = null;
    this.formFeatures = [];
    this.formUserStories = [];

    if (this.selectedFormEpicId) {
      this.loadFormFeatures(this.selectedFormEpicId);
    }
  }

  loadFormFeatures(epicId: number, callback?: () => void) {
    this.featureService
      .getAllFeatures({
        client_id: this.localStorage.getClientId(),
        epic_id: epicId,
        limit: 1000,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.formFeatures = res?.rows || res?.data?.rows || res?.data || [];
          if (callback) callback();
        },
      });
  }

  onFormFeatureChange(featureId: any) {
    this.selectedFormFeatureId = featureId ? Number(featureId) : null;
    this.editData.user_story_id = null;
    this.formUserStories = [];

    if (this.selectedFormFeatureId) {
      this.loadFormUserStories(this.selectedFormFeatureId);
    }
  }

  loadFormUserStories(featureId: number) {
    this.userStoryService
      .getAllUserStories({
        client_id: this.localStorage.getClientId(),
        feature_id: featureId,
        limit: 1000,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.formUserStories =
            res?.rows || res?.data?.rows || res?.data || [];
        },
      });
  }

  onImgError(event: any) {
    event.target.src = 'assets/img/fallback-image.png';
  }
}
